import { useState, useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { Edit, Trash2, Mail, VideoOff, Globe } from 'lucide-preact'
import type { ClientWorkspaceViewDto, WorkspaceUpdateDto } from '@/types/company'
import { StatusBadge } from '../../common/StatusBadge'
import { FormActions } from '@/components'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { WorkspaceFormFields } from './WorkspaceFormFields'
import type { CompanyWebsite } from './workspaceWebsite'
import { websiteLabel } from '@/utils/websiteLabel'

/** How a save went. A refusal keeps the API's status and errors, so each can be shown at its field. */
export type WorkspaceSaveOutcome = { ok: true } | { ok: false; status: number; error?: string }

interface WorkspaceCardProps {
  workspace: ClientWorkspaceViewDto
  /** The company's own website, which this workspace uses while it has none of its own (ORB-1027). */
  company: CompanyWebsite
  onDelete: (workspaceId: string) => void
  onUpdate: (workspace: WorkspaceUpdateDto) => Promise<WorkspaceSaveOutcome>
  loading?: boolean
}

export function WorkspaceCard({ workspace, company, onDelete, onUpdate, loading = false }: WorkspaceCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editingWorkspace, setEditingWorkspace] = useState<WorkspaceUpdateDto | null>(null)

  type FormValues = { Name: string; Slug: string; Website: string; Description: string; Status: string; TimeZone: string; DailyLeadsSummaryEnabled: boolean; DailyLeadsSummaryHour: number; WebsterEnabled: boolean; PostWebinarSummaryEnabled: boolean; StartOfWeekSummaryEnabled: boolean; StartOfWeekSummaryDayOfWeek: number; StartOfWeekSummaryHour: number }
  const { register, control, handleSubmit, reset, watch, setValue, setError, formState: { errors, isValid, isDirty } } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { Name: '', Slug: '', Website: '', Description: '', Status: 'Active', TimeZone: '', DailyLeadsSummaryEnabled: false, DailyLeadsSummaryHour: 8, WebsterEnabled: true, PostWebinarSummaryEnabled: false, StartOfWeekSummaryEnabled: false, StartOfWeekSummaryDayOfWeek: 1, StartOfWeekSummaryHour: 8 },
  })

  const handleEdit = () => {
    const initial: WorkspaceUpdateDto = {
      Id: workspace.Id,
      ETag: workspace.ETag,
      ClientCompanyId: workspace.ClientCompanyId,
      Name: workspace.Name,
      Slug: workspace.Slug,
      Website: workspace.Website ?? '',
      Description: workspace.Description || '',
      Status: workspace.Status || 'Active',
      TimeZone: workspace.TimeZone,
      DailyLeadsSummaryEnabled: workspace.DailyLeadsSummaryEnabled,
      DailyLeadsSummaryHour: workspace.DailyLeadsSummaryHour,
      WebsterEnabled: workspace.WebsterEnabled ?? true,
      PostWebinarSummaryEnabled: workspace.PostWebinarSummaryEnabled ?? false,
      StartOfWeekSummaryEnabled: workspace.StartOfWeekSummaryEnabled ?? false,
      StartOfWeekSummaryDayOfWeek: workspace.StartOfWeekSummaryDayOfWeek ?? 1,
      StartOfWeekSummaryHour: workspace.StartOfWeekSummaryHour ?? 8,
    }
    setEditingWorkspace(initial)
    reset({
      Name: initial.Name,
      Slug: initial.Slug ?? '',
      Website: initial.Website ?? '',
      Description: initial.Description || '',
      Status: initial.Status || 'Active',
      TimeZone: initial.TimeZone,
      DailyLeadsSummaryEnabled: initial.DailyLeadsSummaryEnabled,
      DailyLeadsSummaryHour: initial.DailyLeadsSummaryHour,
      WebsterEnabled: initial.WebsterEnabled ?? true,
      PostWebinarSummaryEnabled: initial.PostWebinarSummaryEnabled ?? false,
      StartOfWeekSummaryEnabled: initial.StartOfWeekSummaryEnabled ?? false,
      StartOfWeekSummaryDayOfWeek: initial.StartOfWeekSummaryDayOfWeek ?? 1,
      StartOfWeekSummaryHour: initial.StartOfWeekSummaryHour ?? 8,
    })
    setIsEditing(true)
  }

  const onSubmit = async (values: FormValues) => {
    if (!editingWorkspace) return
    const payload: WorkspaceUpdateDto = {
      ...editingWorkspace,
      Name: values.Name,
      // Always sent: an empty one clears the workspace's own website, where an absent one would keep it.
      Website: values.Website.trim(),
      Description: values.Description,
      Status: values.Status,
      TimeZone: values.TimeZone,
      DailyLeadsSummaryEnabled: values.DailyLeadsSummaryEnabled,
      DailyLeadsSummaryHour: Number(values.DailyLeadsSummaryHour),
      WebsterEnabled: values.WebsterEnabled,
      PostWebinarSummaryEnabled: values.PostWebinarSummaryEnabled,
      StartOfWeekSummaryEnabled: values.StartOfWeekSummaryEnabled,
      StartOfWeekSummaryDayOfWeek: Number(values.StartOfWeekSummaryDayOfWeek),
      StartOfWeekSummaryHour: Number(values.StartOfWeekSummaryHour),
    }
    const outcome = await onUpdate(payload)
    if (outcome.ok) {
      setIsEditing(false)
      setEditingWorkspace(null)
    } else {
      setFormValidationErrors(outcome, setError)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditingWorkspace(null)
  }

  // Keep local editingWorkspace in sync with form watch values
  const wName = watch('Name')
  const wWebsite = watch('Website')
  const wDesc = watch('Description')
  const wStatus = watch('Status')
  const wTimeZone = watch('TimeZone')
  const wDailyLeads = watch('DailyLeadsSummaryEnabled')
  const wDailyHour = watch('DailyLeadsSummaryHour')
  const wWebster = watch('WebsterEnabled')
  const wPostWebinar = watch('PostWebinarSummaryEnabled')
  const wStartOfWeek = watch('StartOfWeekSummaryEnabled')
  const wStartOfWeekDay = watch('StartOfWeekSummaryDayOfWeek')
  const wStartOfWeekHour = watch('StartOfWeekSummaryHour')
  useEffect(() => {
    if (isEditing && editingWorkspace) {
      setEditingWorkspace({
        ...editingWorkspace,
        Name: wName || '',
        Website: wWebsite ?? '',
        Description: wDesc || '',
        Status: wStatus || 'Active',
        TimeZone: wTimeZone || '',
        DailyLeadsSummaryEnabled: wDailyLeads,
        DailyLeadsSummaryHour: Number(wDailyHour),
        WebsterEnabled: wWebster,
        PostWebinarSummaryEnabled: wPostWebinar,
        StartOfWeekSummaryEnabled: wStartOfWeek,
        StartOfWeekSummaryDayOfWeek: Number(wStartOfWeekDay),
        StartOfWeekSummaryHour: Number(wStartOfWeekHour),
      })
    }
  }, [wName, wWebsite, wDesc, wStatus, wTimeZone, wDailyLeads, wDailyHour, wWebster, wPostWebinar, wStartOfWeek, wStartOfWeekDay, wStartOfWeekHour])

  return (
    <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-200">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                  <WorkspaceFormFields control={control} register={register} errors={errors} watch={watch} setValue={setValue} slugLocked showEditOnlyToggles website={company} />
                  <FormActions
                    isSubmitting={loading}
                    isSaveDisabled={!isValid || !isDirty}
                    onCancel={handleCancel}
                    saveText="Save Changes"
                    className="px-0 py-1 border-0 bg-transparent dark:bg-transparent"
                  />
                </form>
              ) : (
                <>
                  <h4 className="font-semibold text-slate-900 dark:text-white text-lg">{workspace.Name}</h4>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-sm text-slate-600 dark:text-slate-300 line-clamp-1">{workspace.Description || 'No description'}</span>
                    <StatusBadge status={workspace.Status || 'Unknown'} variant="light" />
                    {workspace.TimeZone && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{workspace.TimeZone}</span>
                    )}
                  </div>
                  <WebsiteLine website={workspace.Website ?? null} company={company} />
                  {workspace.DailyLeadsSummaryEnabled && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Mail className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                      <span className="text-xs text-slate-600 dark:text-slate-400">Daily leads summary enabled</span>
                    </div>
                  )}
                  {workspace.PostWebinarSummaryEnabled && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Mail className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                      <span className="text-xs text-slate-600 dark:text-slate-400">Post-webinar summary enabled</span>
                    </div>
                  )}
                  {workspace.StartOfWeekSummaryEnabled && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Mail className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                      <span className="text-xs text-slate-600 dark:text-slate-400">Start-of-week summary enabled</span>
                    </div>
                  )}
                  {workspace.WebsterEnabled === false && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <VideoOff className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                      <span className="text-xs text-slate-500 dark:text-slate-400">Webster Notetaker off</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-3">
            {!isEditing && (
              <>
                <button onClick={handleEdit} className="p-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors" title="Edit Workspace">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(workspace.Id)} className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete Workspace">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Which website the workspace uses: its own, or the company's while it has none (ORB-1027). */
function WebsiteLine({ website, company }: { website: string | null; company: CompanyWebsite }) {
  const companyHost = websiteLabel(company.companyWebsite)
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <Globe className="w-3.5 h-3.5 flex-none text-slate-500 dark:text-slate-400" aria-hidden="true" />
      {website ? (
        <span className="text-xs text-slate-600 dark:text-slate-300">
          Its own website,{' '}
          <a href={website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-700 dark:text-primary-400 hover:underline">
            {websiteLabel(website) ?? website}
          </a>
        </span>
      ) : (
        <span className="text-xs text-slate-600 dark:text-slate-300">
          Uses {company.companyName}'s website{companyHost ? `, ${companyHost}` : ''}
        </span>
      )}
    </div>
  )
}
